/**
 * GameOverModal - End game experience for chess
 *
 * Displays:
 * - Victory/Defeat/Draw status
 * - Game statistics
 * - Share on X button
 * - Play Again / View Games buttons
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChessGame, ChessColor } from './types';
import './GameOverModal.css';

export interface GameOverModalProps {
    game: ChessGame;
    gameId: string;
    playerId: string | null;
    didIWin: boolean;
    didIDraw: boolean;
    endReason: string | null;
    onPlayAgain: () => void;
    onClose?: () => void;
}

export const GameOverModal: React.FC<GameOverModalProps> = ({
    game,
    gameId,
    playerId,
    didIWin,
    didIDraw,
    endReason,
    onPlayAgain,
    onClose
}) => {
    const navigate = useNavigate();

    // Format end reason for display
    const formatEndReason = (reason: string | null): string => {
        if (!reason) return '';
        switch (reason) {
            case 'checkmate': return 'Checkmate';
            case 'resignation': return 'Resignation';
            case 'timeout': return 'Time Out';
            case 'abandoned': return 'Abandoned';
            case 'stalemate': return 'Stalemate';
            case 'draw': return 'Draw';
            default: return reason.charAt(0).toUpperCase() + reason.slice(1);
        }
    };

    // Calculate game duration (if available)
    const calculateDuration = (): string => {
        // Approximate based on move count and time used
        const moveCount = game.moves?.length || 0;
        if (moveCount === 0) return '< 1 min';

        // Rough estimate: average 30 seconds per move
        const estimatedMinutes = Math.max(1, Math.floor(moveCount * 0.5));
        if (estimatedMinutes < 60) {
            return `~${estimatedMinutes} min`;
        }
        const hours = Math.floor(estimatedMinutes / 60);
        const mins = estimatedMinutes % 60;
        return `~${hours}h ${mins}m`;
    };

    // Get my player info
    const myPlayer = game.players.find(p => p.id === playerId);
    const opponentPlayer = game.players.find(p => p.id !== playerId);
    const myColor = myPlayer?.color === ChessColor.PLAIN ? 'White' : 'Black';

    // Share on X
    const handleShare = () => {
        const replayUrl = `${window.location.origin}/mferchess/replay/${gameId}`;
        const opponentName = opponentPlayer?.socketId?.startsWith('AI') ? 'the computer' : 'my opponent';

        let resultText = '';
        if (didIDraw) {
            resultText = `drew against ${opponentName}`;
        } else if (didIWin) {
            resultText = `defeated ${opponentName}`;
        } else {
            resultText = `played against ${opponentName}`;
        }

        const moveCount = game.moves?.length || 0;
        const reasonText = endReason ? ` by ${formatEndReason(endReason).toLowerCase()}` : '';

        const tweetText = `I just ${resultText} in mfer chess${reasonText}!\n\n` +
            `${moveCount} moves | Playing as ${myColor}\n\n` +
            `Check out the replay:`;

        const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(replayUrl)}`;
        window.open(twitterIntentUrl, '_blank', 'noopener,noreferrer');
    };

    // Navigate handlers
    const handleViewReplay = () => {
        navigate(`/mferchess/replay/${gameId}`);
    };

    const handleViewGames = () => {
        navigate('/mferchess');
    };

    const handleBackToArcade = () => {
        navigate('/');
    };

    // Determine result display
    const getResultDisplay = () => {
        if (!playerId) {
            return { text: 'Game Over', emoji: '', className: 'spectator' };
        }
        if (didIDraw) {
            return { text: 'Draw!', emoji: '', className: 'draw' };
        }
        if (didIWin) {
            return { text: 'Victory!', emoji: '', className: 'victory' };
        }
        return { text: 'Defeat', emoji: '', className: 'defeat' };
    };

    const result = getResultDisplay();

    return (
        <div className="game-over-overlay" onClick={onClose}>
            <div className="game-over-modal" onClick={e => e.stopPropagation()}>
                {/* Result Header */}
                <div className={`game-over-result ${result.className}`}>
                    <span className="result-emoji">{result.emoji}</span>
                    <h2 className="result-text">{result.text}</h2>
                </div>

                {/* End Reason */}
                {endReason && (
                    <p className="game-over-reason">
                        {formatEndReason(endReason)}
                    </p>
                )}

                {/* Game Stats */}
                <div className="game-over-stats">
                    <div className="stat">
                        <span className="stat-label">Moves</span>
                        <span className="stat-value">{game.moves?.length || 0}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Duration</span>
                        <span className="stat-value">{calculateDuration()}</span>
                    </div>
                    {myPlayer && (
                        <div className="stat">
                            <span className="stat-label">Played As</span>
                            <span className="stat-value">{myColor}</span>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="game-over-actions">
                    <button
                        className="action-btn primary"
                        onClick={onPlayAgain}
                    >
                        Play Again
                    </button>

                    <button
                        className="action-btn secondary"
                        onClick={handleViewReplay}
                    >
                        View Replay
                    </button>

                    <button
                        className="action-btn share"
                        onClick={handleShare}
                    >
                        <img
                            src="/images/x-logo-black.png"
                            alt="X"
                            className="x-logo"
                        />
                        Share on X
                    </button>

                    <button
                        className="action-btn tertiary"
                        onClick={handleViewGames}
                    >
                        View Games
                    </button>

                    <button
                        className="action-btn tertiary"
                        onClick={handleBackToArcade}
                    >
                        Back to Arcade
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GameOverModal;
